/*
 * File: core.ts
 * File Created: 2023-11-27 03:21:15
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-18 10:52:49
 * Modified By: Val Liu
 * -----
 */

import {
    EUITagTarget,
    IUITagInput,
    IUITagResult,
    IUITagVariables,
} from "@root/core/tags/core";
import { MetaAttribute, MetaParser, ObjectHelper } from "@root/core";
import { IPaging, IQuery } from "@root/core/server/restful";
import { Vue } from "vue-property-decorator";
import { applyTags } from "@root/core/tags/core/apply-tags";
import { performance } from "@root/core/decorators";
import { IBaseComponentUITag, IServer } from "@root/components/interfaces";

export interface IGetResult {
    paging: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
    results: any[];
}

export const SortSequence = ["desc", "asc", null] as const;
export type IHeaderCellSort = (typeof SortSequence)[number];
export interface ITableHeaderCellAttr {
    sortable: boolean;
    syncedSort: IHeaderCellSort;
    metaUnit: MetaAttribute;
}

export interface ITableBodyCellAttr {
    paging: IPaging;
    rows: any[];
    row: any;

    index: number;
    name: string;
    value: any;
}

export function getCellProperties(
    target: keyof typeof EUITagTarget,
    query: IQuery<any>,
    index?: number,
    metas?: MetaParser,
    metaIndex?: number,
    component?: Vue,
    uiTag?: IBaseComponentUITag,
    server?: IServer,
    table?: Vue,
    element?: Vue,
): IUITagResult {
    do {
        const data = query.data;
        if (metas == null || metaIndex == null) break;
        const meta = metas.getMetaByIndex(metaIndex);
        if (meta == null) break;
        const name = meta.attr.name;
        const rows = data?.results;
        const row = index != null ? rows?.[index] : null;
        const value = row?.[name];

        /// initial variables.
        const variables: IUITagVariables = {
            server,
            hostElement: table,
            element,
        };
        /// initial props
        const baseProps: IUITagResult = {
            props: {
                paging: data?.paging,
                rows,
                row,

                index,
                name,
                value,
            },
            styles: {},
            specials: {},
        };

        /// initial input
        const baseInput: Omit<IUITagInput, "rule"> = {
            /// rule
            target: EUITagTarget[target],
            metas,
            metaIndex,
            meta,
            inputs: baseProps,
            isDefault: false,
            variables,
            component,
        };
        let finalProps = applyTags(EUITagTarget[target], baseInput, uiTag);

        /// eventually, merge the styles
        return {
            ...finalProps.inputs,
            props: ObjectHelper.deepMerge(finalProps.inputs.props, {
                style: finalProps.inputs.styles,
            }),
        };
    } while (0);

    return {};
}

/// TableBody
export interface ITableBodyData {
    "text-no-data": string;
}
export const defaultBodyData: ITableBodyData = {
    "text-no-data": "w_NoDataAvailable",
};

/// TablePagination
export interface ITablePaginationData {
    "text-NextPage": string;
    "text-PreviousPage": string;
    "text-Search": string;
    "text-ShowEntries": string;
}
export interface ITablePaginationPortalData {
    "portal-pagination"?: string;
    "portal-search"?: string;
}
export const defaultPaginationData: ITablePaginationData = {
    "text-NextPage": "w_NextPage",
    "text-PreviousPage": "w_PreviousPage",
    "text-Search": "w_Search",
    "text-ShowEntries": "w_ShowEntries",
};

/// TablePopover
export interface ITablePopoverData {
    /// deprecated
    // "custom-popover-class"?: string;
}
export const defaultPopoverData: ITablePopoverData = {};
